import { useQuery, useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { t } from 'i18next';
import { Link } from 'react-router-dom';

import {
  CREATE_REFERRAL_CODE,
  GET_REFERRAL_BALANCE,
  GET_SIGNUP_REFERRAL_CODE,
  LIST_REDEEMED_REFERRAL_CODES,
  LIST_REFERRAL_CREDITS,
} from '../../http/apollo-queries';

import { useThemeContext } from '../../context/Theme';
import { useAuth } from '../../context/Auth';

import { AccountNav } from '../Account/AccountNav';
import PopupWindow from '../../components/Popup-window/Popup-window';
import { Button, Card, Table, Icon } from '../../components/UI';
import RevealButton from '../../components/Reveal-button/Reveal-button';

import FacebookShareButton from './FacebookShareButton';
import TwitterTweetButton from './TwitterTweetButton';
import EmailShareButton from './EmailShareButton';

import { formatDate } from '../../helpers/functions';
import { CurrencyCodeEnum } from '../../constants/enums';

const Referral = () => {
  const { user } = useAuth();
  const [
    createReferralCode,
    { loading: referralCodeLoading, data: referralCodeData },
  ] = useMutation(CREATE_REFERRAL_CODE);
  const { data: referralBalanceData } = useQuery(GET_REFERRAL_BALANCE);
  const { data: signupReferralCodeData } = useQuery(GET_SIGNUP_REFERRAL_CODE);
  const { data: redeemedReferralCodesData } = useQuery(
    LIST_REDEEMED_REFERRAL_CODES
  );
  const { data: referralCreditsData } = useQuery(LIST_REFERRAL_CREDITS);

  const cumulativePercentage = 20;
  const availablePercentages = [0, 5, 10, 15, 20];

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: CurrencyCodeEnum.USD,
  });

  const [chosenPercentage, setChosenPercentage] = useState(15);
  const [redeemerCreditPercentage, setRedeemerCreditPercentage] = useState(5);

  const [showGeneratedLinkPopup, setShowGeneratedLinkPopup] = useState(false);
  const [createdReferralCode, setCreatedReferralCode] = useState('');
  const [ownerCreditPercentage, setOwnerCreditPercentage] = useState(0);

  const [totalReferralBalance, setTotalReferralBalance] = useState(0);
  const [redeemedReferralBalance, setRedeemedReferralBalance] = useState(0);
  const [availableReferralBalance, setAvailableReferralBalance] = useState(0);

  const [signupCode, setSignupCode] = useState<string[][]>([]);
  const [redeemedReferralCodes, setRedeemedReferralCodes] = useState<
    string[][]
  >([]);
  const [referralCredits, setReferralCredits] = useState<string[][]>([]);

  const [isMoreRevealed, setIsMoreRevealed] = useState(false);

  useEffect(() => {
    if (referralCodeData) {
      const { createReferralCode } = referralCodeData;
      const { code, ownerCreditPercentage } = createReferralCode;

      setCreatedReferralCode(code);
      setOwnerCreditPercentage(ownerCreditPercentage);
      setShowGeneratedLinkPopup(true);
    }
  }, [referralCodeData]);

  useEffect(() => {
    if (referralBalanceData) {
      const { getReferralBalance } = referralBalanceData;
      const { total, redeemed, available } = getReferralBalance;
      setTotalReferralBalance(total);
      setRedeemedReferralBalance(redeemed);
      setAvailableReferralBalance(available);
    }
  }, [referralBalanceData]);

  useEffect(() => {
    if (
      signupReferralCodeData &&
      signupReferralCodeData.getSignupReferralCode
    ) {
      const {
        getSignupReferralCode: {
          ownerUser: { name },
          subscribedAt,
          redeemed,
          ownerCredit,
          ownerCreditPercentage,
          redeemerCredit,
          redeemerCreditPercentage,
        },
      } = signupReferralCodeData;

      setSignupCode([
        [
          name,
          formatDate(subscribedAt),
          redeemed ? 'Redeemed' : 'Not redeemed',
          formatter.format(ownerCredit) + ' (' + ownerCreditPercentage + '%)',
          formatter.format(redeemerCredit) +
            ' (' +
            redeemerCreditPercentage +
            '%)',
        ],
      ]);
    }
  }, [signupReferralCodeData]);

  useEffect(() => {
    if (redeemedReferralCodesData) {
      const { listRedeemedReferralCodes } = redeemedReferralCodesData;

      const tempRedeemedReferralCodes = listRedeemedReferralCodes.items.map(
        (item) => {
          return [
            item.redeemerUser.name,
            formatDate(item.subscribedAt),
            item.membershipPlan.title,
            formatter.format(item.redeemerCredit) +
              ' (' +
              item.redeemerCreditPercentage +
              '%)',
            formatter.format(item.ownerCredit) +
              ' (' +
              item.ownerCreditPercentage +
              '%)',
          ];
        }
      );

      setRedeemedReferralCodes(tempRedeemedReferralCodes);
    }
  }, [redeemedReferralCodesData]);

  useEffect(() => {
    if (referralCreditsData) {
      const { listReferralCredits } = referralCreditsData;
      const items = listReferralCredits.items;

      const tempRedeemedReferralCodes = Array<any[]>();

      items.map((item) => {
        tempRedeemedReferralCodes.push([
          formatDate(item.usedAt),
          item.id,
          item.membershipName,
          formatter.format(item.discount),
          formatter.format(item.amount),
        ]);
      });

      setReferralCredits(tempRedeemedReferralCodes);
    }
  }, [referralCreditsData]);

  const createReferralCodeHandler = () => {
    createReferralCode({
      variables: {
        ownerCreditPercentage: chosenPercentage,
        redeemerCreditPercentage: redeemerCreditPercentage,
        ownerProfileId: user?.profile?.id,
      },
    });
  };

  return (
    <div className="flex flex-col w-full bg-white items-center text-black dark:bg-black-real dark:text-white">
      <div className="w-full lg:w-8/12X">
        <AccountNav />
        {showGeneratedLinkPopup && (
          <PopupWindow
            onCloseClick={() => setShowGeneratedLinkPopup(false)}
            popupIcon="icons/social-sharing 1.svg"
            title={t('referral.shareTitle')}
            subtitle={t('referral.shareDescription', {
              percentage: ownerCreditPercentage,
            })}
          >
            <div className="flex justify-between items-center w-88 h-11 rounded-lg border border-grey-10 px-3 mt-8 mb-4">
              <span className="text-sm">{createdReferralCode}</span>
              <Icon
                svgName={'copy'}
                className="cursor-pointer"
                onClick={() => {
                  navigator.clipboard.writeText(createdReferralCode);
                }}
              />
            </div>
            <div className="flex gap-2">
              <TwitterTweetButton referralCode={createdReferralCode} />
              <FacebookShareButton referralCode={createdReferralCode} />
              <EmailShareButton referralCode={createdReferralCode} />
            </div>
          </PopupWindow>
        )}

        {user?.membership?.paid && user?.membership?.source === 'MOBILE' ? (
          <>
            <Card title={t('referral.cardTitle')}>
              {t('referral.mobileWarning')}
            </Card>
          </>
        ) : (
          <>
            <Card
              className="mt-4"
              title={t('referral.cardTitle')}
              padding={false}
            >
              <div className="flex flex-col md:flex-row w-full">
                <div className="flex flex-col justify-between border-r border-opacity-50 border-grey-30 dark:border-grey-70 p-5">
                  <div>
                    <p className="font-bold text-2xl mb-4">
                      {t('referral.title')}
                    </p>
                    <p className="text-md text-grey-70 mb-4">
                      {t('referral.description')}
                    </p>
                  </div>
                  <div>
                    <Link
                      className="text-grey-50 text-sm underline"
                      to="/privacy-and-terms"
                    >
                      {t('referral.details')}
                    </Link>
                  </div>
                </div>

                <div className="flex flex-col p-5">
                  <p className="font-bold text-lg mb-1">
                    {t('referral.chooseYourDiscount')}
                  </p>
                  <p className="text-grey-50 text-xs mb-4">
                    {t('referral.discountDescription')}
                  </p>
                  <p className="text-sm mb-3">{t('referral.youWillGet')}</p>
                  <div className="flex gap-2 mb-4">
                    {availablePercentages.map((percentage) => {
                      const buttonStyle =
                        chosenPercentage === percentage
                          ? 'bg-grey-5 border-grey-50 dark:bg-grey-80 dark:border-white'
                          : 'border-grey-10 dark:bg-grey-80 dark:border-grey-70';
                      return (
                        <button
                          key={percentage}
                          className={`flex justify-center items-center border ${buttonStyle} font-semibold rounded-xl w-16 h-10 px-3`}
                          onClick={() => {
                            setChosenPercentage(percentage);
                            setRedeemerCreditPercentage(
                              cumulativePercentage - percentage
                            );
                          }}
                        >
                          {percentage}%
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-sm mb-3">
                    {t('referral.yourFriendWillGet')}
                  </p>
                  <div className="flex gap-2 mb-4">
                    <button className="flex justify-center items-center border bg-grey-5 dark:bg-grey-80 dark:border-grey-70 font-semibold rounded-xl w-16 h-10 px-3 cursor-default">
                      {redeemerCreditPercentage}%
                    </button>
                  </div>
                  <Button
                    type="button"
                    size="small"
                    variant="primary"
                    buttonClass="w-48"
                    loading={referralCodeLoading}
                    onClick={createReferralCodeHandler}
                  >
                    {t('referral.generateLink')}
                  </Button>
                </div>
              </div>
            </Card>
            <Card
              className="my-4"
              title={t('referral.yourCredits')}
              padding={false}
            >
              <div className="flex flex-col md:flex-row w-full p-5 mb-4">
                <div>
                  <p className="font-bold text-lg mb-2">
                    {t('referral.availableCredits')}
                  </p>
                  <div className="flex justify-center items-center rounded-lg bg-grey-5 w-48 h-28 dark:bg-grey-80">
                    <span className="font-bold text-xl">
                      {formatter.format(availableReferralBalance)}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col justify-center ml-0 md:ml-36 mt-4 md:mt-0 ">
                  <p className="font-bold">{t('referral.howUseCredits')}</p>
                  <p className="text-sm">{t('referral.howUseCreditsAnswer')}</p>
                </div>
              </div>
              <div className="border-t border-opacity-50 border-grey-30 dark:border-grey-70">
                {!!signupCode.length && (
                  <Table
                    columns={[
                      t('referral.acceptedInvitation'),
                      t('referral.date'),
                      t('referral.status'),
                      t('referral.friendCredits'),
                      t('referral.yourCredits'),
                    ]}
                    data={signupCode}
                    emptyTableMessage={t('referral.noCredits')}
                  />
                )}
                <Table
                  columns={[
                    t('referral.invitedUsers'),
                    t('referral.date'),
                    t('referral.planPurchased'),
                    t('referral.friendCredits'),
                    t('referral.yourCredits'),
                  ]}
                  data={redeemedReferralCodes}
                  emptyTableMessage={t('referral.noCredits')}
                />
              </div>
              <div className="flex justify-end w-full py-5 px-20">
                <RevealButton
                  size="small"
                  revealedLabel={t('referral.lessDetails')}
                  notRevealedLabel={t('referral.moreDetails')}
                  setIsMoreRevealed={setIsMoreRevealed}
                />
              </div>
              {isMoreRevealed && (
                <>
                  <div className="flex justify-center gap-8 mb-5">
                    <div className="flex flex-col items-start rounded-lg bg-grey-5 w-40 p-3 dark:bg-grey-80">
                      <span className="font-semibold text-grey-50 text-md">
                        {t('referral.total')}
                      </span>
                      <span className="font-semibold text-lg">
                        {formatter.format(totalReferralBalance)}
                      </span>
                    </div>
                    <div className="flex flex-col items-start rounded-lg bg-grey-5 w-40 p-3 dark:bg-grey-80">
                      <span className="font-semibold text-grey-50 text-md">
                        {t('referral.redeemed')}
                      </span>
                      <span className="font-semibold text-lg">
                        {formatter.format(redeemedReferralBalance)}
                      </span>
                    </div>
                    <div className="flex flex-col items-start rounded-lg bg-grey-5 w-40 p-3 dark:bg-grey-80">
                      <span className="font-semibold text-grey-50 text-md">
                        {t('referral.available')}
                      </span>
                      <span className="font-semibold text-lg">
                        {formatter.format(availableReferralBalance)}
                      </span>
                    </div>
                  </div>
                  <div className="flex border-t border-b border-opacity-50 border-grey-30 dark:border-grey-70 py-3 px-5">
                    <div className="flex justify-start w-full uppercase font-bold text-sm">
                      <span className="">{t('referral.usageHistory')}</span>
                    </div>
                  </div>
                  <Table
                    columns={[
                      t('referral.date'),
                      t('referral.receiptNumber'),
                      t('referral.planPurchased'),
                      t('referral.discountApplied'),
                      t('referral.amountPaid'),
                    ]}
                    data={referralCredits}
                    emptyTableMessage={t('referral.noCredits')}
                  />
                </>
              )}
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default Referral;
